import {
  ACTIONFLOW_USER_OUTCOME_CONTRACT_SCHEMA_VERSION,
  type ActionFlowUserOutcomeContract,
  type ActionFlowUserOutcomeCriterion,
} from "./actionflow-user-outcome-proof";
import type {
  ActionFlowTopologySimulationProof,
  ActionFlowTopologySimulationStep,
} from "./actionflow-topology-simulation";

export interface DeriveActionFlowUserOutcomeContractOptions {
  scenarioId: string;
  userGoal: string;
  userValue: string;
  persona?: string;
  simulationProof: ActionFlowTopologySimulationProof;
  explicitCriteria?: ActionFlowUserOutcomeCriterion[];
  evidenceRefs?: string[];
  maxDerivedCriteria?: number;
}

export function deriveActionFlowUserOutcomeContract(
  options: DeriveActionFlowUserOutcomeContractOptions,
): ActionFlowUserOutcomeContract {
  const criteria = [
    simulationOkCriterion(),
    ...(options.explicitCriteria ?? []),
    ...deriveCriteriaFromSimulation(
      options.simulationProof,
      options.maxDerivedCriteria ?? 8,
    ),
  ];
  return {
    schemaVersion: ACTIONFLOW_USER_OUTCOME_CONTRACT_SCHEMA_VERSION,
    scenarioId: options.scenarioId,
    userGoal: options.userGoal,
    userValue: options.userValue,
    ...(options.persona ? { persona: options.persona } : {}),
    acceptanceCriteria: dedupeCriteria(criteria),
    evidenceRefs: options.evidenceRefs ?? [options.scenarioId],
  };
}

function simulationOkCriterion(): ActionFlowUserOutcomeCriterion {
  return {
    id: "simulation-ok",
    label: "ActionFlow simulation succeeds",
    kind: "simulation-ok",
    evidenceRefs: ["actionFlowTopologySimulationProof.ok"],
  };
}

function deriveCriteriaFromSimulation(
  simulationProof: ActionFlowTopologySimulationProof,
  maxCriteria: number,
): ActionFlowUserOutcomeCriterion[] {
  const criteria: ActionFlowUserOutcomeCriterion[] = [];
  for (const step of simulationProof.steps) {
    if (step.status === "blocked" || step.status === "skipped" || step.status === "error") {
      criteria.push(stepStatusCriterion(step));
    }
    for (const change of step.stateChanges) {
      if (criteria.length >= maxCriteria) return criteria;
      criteria.push(stateChangeCriterion(change.path, change.after, step));
    }
  }
  return criteria.slice(0, maxCriteria);
}

function stateChangeCriterion(
  path: string,
  value: unknown,
  step: ActionFlowTopologySimulationStep,
): ActionFlowUserOutcomeCriterion {
  const id = `state:${normalizeId(path)}`;
  const evidenceRefs = [path, step.primitiveRef];
  if (Array.isArray(value)) {
    return {
      id,
      label: `${path} has expected length`,
      kind: "state-path-length",
      path,
      length: value.length,
      evidenceRefs,
    };
  }
  return {
    id,
    label: `${path} equals expected value`,
    kind: "state-path-equals",
    path,
    expected: value,
    evidenceRefs,
  };
}

function stepStatusCriterion(
  step: ActionFlowTopologySimulationStep,
): ActionFlowUserOutcomeCriterion {
  return {
    id: `step:${normalizeId(step.primitiveRef)}:${step.status}`,
    label: `${step.primitiveRef} status is ${step.status}`,
    kind: "step-status",
    primitiveRef: step.primitiveRef,
    status: step.status,
    evidenceRefs: [step.primitiveRef],
  };
}

function dedupeCriteria(
  criteria: ActionFlowUserOutcomeCriterion[],
): ActionFlowUserOutcomeCriterion[] {
  const seen = new Set<string>();
  return criteria.filter((criterion) => {
    if (seen.has(criterion.id)) return false;
    seen.add(criterion.id);
    return true;
  });
}

function normalizeId(value: string): string {
  return value.replace(/[^a-zA-Z0-9_-]+/g, "-").replace(/^-|-$/g, "");
}
